import { apiClient } from './client';

/**
 * 인사기록 — 포상 · 징계 API (인사, migration 133 · 시안 A20).
 *
 * 서버: `apps/chat-server/src/modules/hr/awards.routes.ts`.
 *
 * ★★ **포상과 징계는 한 목록이다** — 가르는 것은 `kind` 하나이고, 그 분류(포상/징계/감경)와
 *    말소 기간은 **서버가 SSOT** 다. 화면이 「견책은 1년」 같은 값을 갖지 않는다.
 * ★ **말소일은 서버가 준 값을 그대로 보여 준다** — 화면이 계산하면 취업규칙이 바뀔 때
 *   지난 기록의 날짜까지 소급해 달라진다.
 * ★ **옛 서버에는 이 라우트가 없다**(404). 부르는 쪽이 실패를 삼키고 화면을 접는다.
 */

export const AWARD_KINDS = [
  'COMMENDATION', 'PRIZE', 'REPRIMAND', 'PAY_CUT', 'SUSPENSION', 'MITIGATION', 'CANCELLATION',
] as const;
export type AwardKind = (typeof AWARD_KINDS)[number];

export const AWARD_KIND_LABEL: Record<string, string> = {
  COMMENDATION: '표창', PRIZE: '포상금',
  REPRIMAND: '견책', PAY_CUT: '감봉', SUSPENSION: '정직',
  MITIGATION: '감경', CANCELLATION: '취소',
};

export type AwardCategory = 'REWARD' | 'PUNISHMENT' | 'RELIEF';

/** 등록 폼의 «종류» 고르개가 이 묶음으로 그린다 — 서버의 분류와 같은 값이다. */
export const AWARD_KIND_GROUPS: ReadonlyArray<{ category: AwardCategory; label: string; kinds: AwardKind[] }> = [
  { category: 'REWARD', label: '포상', kinds: ['COMMENDATION', 'PRIZE'] },
  { category: 'PUNISHMENT', label: '징계', kinds: ['REPRIMAND', 'PAY_CUT', 'SUSPENSION'] },
  { category: 'RELIEF', label: '감경 · 취소', kinds: ['MITIGATION', 'CANCELLATION'] },
];

/**
 * 말소 규칙 안내표 — 시안 A20 의 오른쪽 아래 표.
 *
 * ⚠ **이 값은 «보여 주기» 용이다.** 실제 말소일은 서버가 정해 행에 저장하고, 화면은 그 값을
 *   읽기만 한다. 여기 문구를 고쳐도 계산은 바뀌지 않는다(서버 `awards.ts` 가 SSOT).
 */
export const EXPIRY_GUIDE: ReadonlyArray<{ label: string; until: string; onCertificate: string }> = [
  { label: '견책', until: '1년', onCertificate: '안 나감' },
  { label: '감봉', until: '1년', onCertificate: '안 나감' },
  { label: '정직', until: '2년', onCertificate: '안 나감' },
  { label: '표창 · 포상', until: '—', onCertificate: '나감' },
];

export interface AwardDto {
  id: string;
  userId: string;
  name: string | null;
  employeeNumber: string | null;
  teamName: string | null;
  kind: string;
  kindLabel: string;
  category: AwardCategory | null;
  awardDate: string;
  reason: string;
  basis: string | null;
  /** 말소일. 포상은 null 이다. */
  expiresOn: string | null;
  /** 이미 말소됐나 — 행은 남는다. */
  expired: boolean;
  /** 3개월 안에 말소되나. */
  expiringSoon: boolean;
  /** 이 징계를 감경 · 취소한 행의 id. 있으면 효력이 없다. */
  relievedBy: string | null;
  /** 감경 · 취소가 가리키는 원 징계의 id. */
  relatedId: string | null;
  /** 지금 효력이 있는 징계인가. */
  active: boolean;
  /** 증명서에 나가나 — 포상만 true. */
  onCertificate: boolean;
  note: string | null;
  createdAt: string;
}

export interface AwardSummaryDto {
  rewards: number;
  punishments: number;
  reliefs: number;
  rewardBreakdown: Array<{ kind: string; label: string; count: number }>;
  punishmentBreakdown: Array<{ kind: string; label: string; count: number }>;
  /** 앞으로 3개월 안 — **연도와 무관하게 전체**에서 센다. */
  expiringSoon: number;
  activePunishments: number;
}

export interface AwardListDto {
  total: number;
  /** 기록이 있는 해만 온다 — 빈 해를 고르게 두지 않는다. */
  years: string[];
  summary: AwardSummaryDto;
  rows: AwardDto[];
}

export type AwardFilter = 'all' | 'reward' | 'punishment' | 'relief' | 'expiring';

export interface AwardListParams {
  search?: string;
  year?: string;
  filter?: AwardFilter;
}

export async function fetchAwards(params: AwardListParams = {}): Promise<AwardListDto> {
  const q = new URLSearchParams();
  if (params.search?.trim()) q.set('search', params.search.trim());
  if (params.year) q.set('year', params.year);
  if (params.filter && params.filter !== 'all') q.set('filter', params.filter);
  const qs = q.toString();
  const { data } = await apiClient.get<AwardListDto>(`/hr/awards${qs ? `?${qs}` : ''}`);
  return data;
}

export interface UserAwardsDto {
  summary: AwardSummaryDto;
  rows: AwardDto[];
}

/** 한 사람의 이력 — 담당자와 **본인**이 같은 라우트를 쓴다(시안 B2 가 이 자리를 쓴다). */
export async function fetchUserAwards(userId: string): Promise<UserAwardsDto> {
  const { data } = await apiClient.get<UserAwardsDto>(`/hr/awards/user/${userId}`);
  return data;
}

export interface AwardInput {
  userId: string;
  kind: AwardKind;
  awardDate: string;
  reason: string;
  basis?: string | null;
  /** 안 보내면 서버가 종류별 기본값을 채운다. 포상에 보내면 400. */
  expiresOn?: string | null;
  /** 감경 · 취소는 **반드시** 원 징계를 가리켜야 한다. */
  relatedId?: string | null;
  note?: string | null;
}

export async function createAward(input: AwardInput): Promise<AwardDto> {
  const { data } = await apiClient.post<{ award: AwardDto }>('/hr/awards', input);
  return data.award;
}

/** 수정에서는 종류와 대상을 바꾸지 않는다 — 바꿀 일이면 취소하고 새로 등록하는 것이 이력이다. */
export type AwardPatch = Partial<Omit<AwardInput, 'userId' | 'kind'>>;

export async function updateAward(id: string, patch: AwardPatch): Promise<AwardDto> {
  const { data } = await apiClient.patch<{ award: AwardDto }>(`/hr/awards/${id}`, patch);
  return data.award;
}

export async function deleteAward(id: string): Promise<void> {
  await apiClient.delete(`/hr/awards/${id}`);
}
